// Shared readers for a PaymentMatch row of the insurer-payment reconciliation
// review. The backend sends the client as a single `fullName` (ours) plus
// `csvFullName` (what the uploaded file spelled); older responses split the name
// into first/last, so both shapes are read here rather than in every cell.

const joinName = (last, first) => [last || '', first || '']
  .filter(Boolean)
  .join(' ')
  .trim();

// The name as Renova knows it. Empty when we hold no client name for the row.
export function renovaName(row) {
  return (row?.fullName || '').trim() || joinName(row?.lastName, row?.firstName);
}

// The name exactly as the uploaded file spelled it.
export function fileName(row) {
  return (row?.csvFullName || '').trim() || joinName(row?.csvLastName, row?.csvFirstName);
}

// What to show: ours when we have it, the file's spelling otherwise.
export function displayName(row) {
  return renovaName(row) || fileName(row);
}

// True when the shown name is the file's spelling, not ours — it must be marked
// so nobody reads it as a name Renova holds.
export function nameIsFromFile(row) {
  return !renovaName(row) && !!fileName(row);
}

// How the holder's name matched, as an i18n key, or null when there is nothing to say: an exact
// match needs no note, and a mismatch is already its own group. ALIAS is a spelling an operator
// confirmed for this client in an earlier file; APPROXIMATE is close but not the same.
const NAME_MATCH_BADGES = {
  ALIAS: 'paymentsReview.nameMatch.ALIAS',
  APPROXIMATE: 'paymentsReview.nameMatch.APPROXIMATE',
};

export function nameMatchBadge(row) {
  return NAME_MATCH_BADGES[row?.holderNameMatch] || null;
}

// The client page for this row, or null when the row matched no client.
export function clientHref(row) {
  if (row?.personProfileId) return `/admin/users/${row.personProfileId}`;
  if (row?.clientCompanyId) return `/admin/companies/${row.clientCompanyId}`;
  return null;
}

// The policy page for this row, or null for a row that matched no policy.
export function policyHref(row) {
  return row?.policyId ? `/admin/policies/${row.policyId}` : null;
}

// One row's decision as both reconcile endpoints read it.
//
// `decision` is the review's own record -- { intent, target, sameClient } -- and never the backend's
// echo of it. A row goes as SKIP only when the operator skipped it, or while its holder name disagrees
// with ours and nobody has confirmed it is the same client: listing such a row IS that confirmation to
// the backend. Every other row keeps its payment, so an untouched or blocked row still competes for it
// and is released the moment the row it collides with moves.
export function wireDecision(row, decision, blocked) {
  const d = decision || {};
  const base = {rowNumber: row.rowNumber, policyId: row.policyId};
  // `blocked`: a row whose listing would say something the operator has not said -- a name mismatch
  // nobody confirmed, a paid payment nobody chose to write off.
  if (d.intent === 'SKIP' || blocked) return {...base, installmentId: null, action: 'SKIP'};
  const flags = d.writeOff ? {writeOff: true} : {};
  const target = d.target || {};
  if (target.create) {
    return {
      ...base, installmentId: null, action: 'CREATE', ...flags
    };
  }
  if (Array.isArray(target.installmentIds) && target.installmentIds.length >= 2) {
    return {
      ...base, installmentId: null, action: 'SPLIT', installmentIds: target.installmentIds, ...flags
    };
  }
  return {
    ...base,
    installmentId: target.installmentId || null,
    action: 'SETTLE',
    ...flags,
    ...(d.cancelFollowing ? {cancelFollowing: true} : {}),
  };
}
